/**
 * Typed REST client for workspace CRUD — ONE WORKSPACE == ONE TAB.
 *
 * Mirrors the fetch pattern in `lib/api-client.ts`: every call rides the
 * HttpOnly session cookie (`credentials: "include"`), JSON bodies are
 * serialized here, and non-2xx responses surface as a typed `ApiError` with
 * the server's message. The API is the write/authorization surface; the open
 * tab set itself is local-only (see `components/terminal/tabs-store`).
 */

import {
  devToken,
  type CanvasItem,
  type DatasetDefinition,
  type PageManifest,
  type ComponentDefinition,
  type WidgetPlacement as ManifestWidgetPlacement,
} from "@itoresearch/api-client";
import type {
  WidgetOp,
  WorkspaceWidgetRow,
} from "@itoresearch/workspace-local";

import { ApiError } from "./api-client";
import { API_BASE_URL } from "./config";

/** A workspace is either a free-form canvas or a structured page. */
export type WorkspaceKind = "canvas" | "page";

/** Page-level layout rigidity; server default is `grid`. */
export type WorkspaceRigidity = "grid" | "canvas_grid" | "canvas";

export type WorkspaceVisibility = "private" | "organization" | "public";

export type WorkspaceStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

/** A workspace row as returned by the API (camelCase serde). */
export interface WorkspaceSummary {
  workspaceId: string;
  organizationId: string;
  ownerUserId: string;
  kind: WorkspaceKind;
  rigidity: WorkspaceRigidity;
  title: string;
  description?: string | null;
  slug?: string | null;
  visibility: WorkspaceVisibility;
  status: WorkspaceStatus;
  ephemeral: boolean;
  expiresAt?: string | null;
  revision: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** A page of workspaces from `GET /api/v1/workspaces`. */
export interface WorkspacesPage {
  data: WorkspaceSummary[];
  limit: number;
  offset: number;
  total: number;
}

export interface ListWorkspacesParams {
  /** Free-text filter (title/slug/description) for the `+` picker. */
  q?: string;
  kind?: WorkspaceKind;
  status?: WorkspaceStatus;
  limit?: number;
  offset?: number;
}

export interface CreateWorkspaceRequest {
  kind: WorkspaceKind;
  title: string;
  description?: string;
  /** Defaults to `grid` server-side. */
  rigidity?: WorkspaceRigidity;
  visibility?: WorkspaceVisibility;
  /** Ephemeral canvases require `expiresAt` and are swept on TTL. */
  ephemeral?: boolean;
  expiresAt?: string;
}

export interface UpdateWorkspaceRequest {
  title?: string;
  /** `null` clears the description; `undefined` leaves it unchanged. */
  description?: string | null;
  rigidity?: WorkspaceRigidity;
  visibility?: WorkspaceVisibility;
  status?: WorkspaceStatus;
}

/**
 * One placed widget in a batch layout replace (`PUT .../layout`). Coordinates
 * are canvas logical units; `widgetId` preserves an existing row when it is
 * still active (the API mints a fresh id otherwise). Mirrors the server's
 * `LayoutWidget` (`crates/api/src/workspaces/model.rs`).
 */
export interface WorkspaceLayoutWidget {
  widgetId?: string;
  /** Component type for a component row. Mutually exclusive with `widgetRef`. */
  widgetType?: string;
  /** Widget-library key for a widget placement. Mutually exclusive with `widgetType`. */
  widgetRef?: string;
  widgetVersion?: number;
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex?: number;
  props?: unknown;
  bindings?: unknown;
  outputs?: unknown;
  /** Supplied input values for a widget placement. */
  inputValues?: unknown;
  pinned?: boolean;
}

/** Response from `PUT /api/v1/workspaces/{id}/layout`. */
export interface WorkspaceLayoutResponse {
  workspaceId: string;
  revision: number;
  widgets: WorkspaceSummary[];
}

/** Timeout so a hung request can't wedge the picker forever. */
const REQUEST_TIMEOUT_MS = 30_000;

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

function requestSignal(): AbortSignal | undefined {
  return typeof AbortSignal !== "undefined" &&
    typeof AbortSignal.timeout === "function"
    ? AbortSignal.timeout(REQUEST_TIMEOUT_MS)
    : undefined;
}

async function request<T>(
  path: string,
  method: Method,
  body?: unknown,
): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // Local dev / e2e: the API accepts a `Bearer dev.<user>.<org>` token instead
  // of a session cookie, which the browser does not have in dev. Mirrors
  // `components/command-menu/securities-search.ts`. Production attaches no
  // token and rides the HttpOnly cookie via `credentials: "include"`.
  const token = import.meta.env.DEV ? devToken() : undefined;
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: "include",
    headers: Object.keys(headers).length > 0 ? headers : undefined,
    body: body === undefined ? undefined : JSON.stringify(body),
    signal: requestSignal(),
  });

  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { message?: string };
      if (data.message) message = data.message;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new ApiError(res.status, message);
  }

  // 204 (and other empty bodies) carry no JSON to parse.
  const text = await res.text();
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

/** Workspace CRUD over the authenticated `/api/v1/workspaces` endpoints. */
export const workspaces = {
  list: (params?: ListWorkspacesParams) => {
    const query = new URLSearchParams();
    if (params?.q) query.set("q", params.q);
    if (params?.kind) query.set("kind", params.kind);
    if (params?.status) query.set("status", params.status);
    if (params?.limit !== undefined) query.set("limit", String(params.limit));
    if (params?.offset !== undefined)
      query.set("offset", String(params.offset));
    const qs = query.toString();
    return request<WorkspacesPage>(
      `/api/v1/workspaces${qs ? `?${qs}` : ""}`,
      "GET",
    );
  },

  get: (workspaceId: string) =>
    request<WorkspaceSummary>(`/api/v1/workspaces/${workspaceId}`, "GET"),

  create: (input: CreateWorkspaceRequest) =>
    request<WorkspaceSummary>("/api/v1/workspaces", "POST", input),

  update: (workspaceId: string, input: UpdateWorkspaceRequest) =>
    request<WorkspaceSummary>(
      `/api/v1/workspaces/${workspaceId}`,
      "PATCH",
      input,
    ),

  remove: (workspaceId: string) =>
    request<void>(`/api/v1/workspaces/${workspaceId}`, "DELETE"),

  /**
   * Compose and serve the workspace's `PageManifest`
   * (`GET /api/v1/workspaces/{id}/manifest`). This is the server-composed
   * snapshot; live canvas clients replace it with the local-first replica.
   */
  manifest: (workspaceId: string) =>
    request<PageManifest>(`/api/v1/workspaces/${workspaceId}/manifest`, "GET"),

  /**
   * Batch-replace the workspace's active widget set
   * (`PUT /api/v1/workspaces/{id}/layout`), bumping the revision.
   */
  putLayout: (workspaceId: string, widgets: WorkspaceLayoutWidget[]) =>
    request<WorkspaceLayoutResponse>(
      `/api/v1/workspaces/${workspaceId}/layout`,
      "PUT",
      { widgets },
    ),
};

// Thin functional wrappers — convenient for callers that prefer named imports
// over the `workspaces` namespace object (e.g. the command palette).
export const listWorkspaces = (params?: ListWorkspacesParams) =>
  workspaces.list(params);
export const getWorkspace = (workspaceId: string) =>
  workspaces.get(workspaceId);
export const createWorkspace = (input: CreateWorkspaceRequest) =>
  workspaces.create(input);
export const updateWorkspace = (
  workspaceId: string,
  input: UpdateWorkspaceRequest,
) => workspaces.update(workspaceId, input);
export const deleteWorkspace = (workspaceId: string) =>
  workspaces.remove(workspaceId);
/** Fetch the server-composed manifest for a workspace (`GET .../manifest`). */
export const getWorkspaceManifest = (workspaceId: string) =>
  workspaces.manifest(workspaceId);
/** Batch-replace a workspace's layout (`PUT .../layout`). */
export const putWorkspaceLayout = (
  workspaceId: string,
  widgets: WorkspaceLayoutWidget[],
) => workspaces.putLayout(workspaceId, widgets);

// ---------------------------------------------------------------------------
// Canvas helpers (pure)
//
// `packages/workspace-local` is the read source of truth for a canvas tab: the
// live Yjs `"widgets"` map yields the widget rows, which are merged onto the
// server-composed manifest so React Flow renders placements with zero latency.
// The functions below are pure (no React / no Yjs) so the merge and the
// optimistic op mapping are unit-testable in isolation.
// ---------------------------------------------------------------------------

/** The subset of a live widget row the canvas merge reads. */
export type CanvasWidgetRow = Pick<
  WorkspaceWidgetRow,
  | "widgetId"
  | "widgetType"
  | "widgetRef"
  | "widgetVersion"
  | "x"
  | "y"
  | "w"
  | "h"
  | "zIndex"
  | "props"
  | "bindings"
  | "outputs"
  | "inputValues"
  | "pinned"
>;

/** A widget's current placement, used to diff `CanvasItem`s into ops. */
export interface WidgetPlacement {
  x: number;
  y: number;
  w: number;
  h: number;
  zIndex?: number | null;
}

/** Coordinates below this delta are treated as unchanged (React Flow sub-pixel noise). */
const PLACEMENT_EPSILON = 0.5;

/**
 * Whether a manifest is a free-form canvas (`canvas` / `canvas_grid`
 * rigidity) whose layout should be driven by the local widget rows. Structured
 * `grid` pages keep the served layout untouched.
 */
export function isCanvasManifest(manifest: PageManifest): boolean {
  return (
    manifest.layout.type === "canvas" ||
    manifest.page.rigidity === "canvas" ||
    manifest.page.rigidity === "canvas_grid"
  );
}

/** A live widget row → the manifest `CanvasItem` React Flow renders. */
export function widgetRowToCanvasItem(row: CanvasWidgetRow): CanvasItem {
  const item: CanvasItem = {
    widget: row.widgetId,
    x: row.x,
    y: row.y,
    w: row.w,
    h: row.h,
  };
  if (row.zIndex !== undefined && row.zIndex !== null) {
    item.zIndex = row.zIndex;
  }
  if (row.pinned !== undefined && row.pinned !== null) {
    item.pinned = row.pinned;
  }
  return item;
}

/** Coerce a JSONB `outputs` value into the protocol's string map (or omit it). */
function outputsRecord(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === "string",
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

/** Copy a JSONB object value (or `undefined` for non-objects/arrays). */
function objectRecord(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value))
    return undefined;
  return { ...(value as Record<string, unknown>) };
}

/**
 * Dataset id + binding reference synthesized for a palette-added
 * `price_chart`. The served manifest predates the locally-added widget, so the
 * merge owns both the dataset declaration and the widget's `series` binding.
 */
export const PRICE_DATASET_ID = "price";
export const PRICE_SERIES_BINDING = "$datasets.price";

/** The instrument symbol a `price_chart` row was added with (or `undefined`). */
function priceChartSymbol(row: CanvasWidgetRow): string | undefined {
  if (row.widgetType !== "price_chart") return undefined;
  const props = objectRecord(row.props);
  const symbol = props?.symbol;
  if (typeof symbol !== "string") return undefined;
  const trimmed = symbol.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** The `series_ref` dataset backing a palette-added `price_chart` of `symbol`. */
function priceSeriesDataset(symbol: string): DatasetDefinition {
  return {
    type: { type: "series_ref" },
    kind: "series",
    source: { symbol },
  };
}

/**
 * Synthesize a `ComponentDefinition` for a locally-added widget that the served
 * manifest does not know about yet (it is already replicated to the server by
 * the write queue, but the manifest snapshot predates it).
 *
 * A `price_chart` carries its symbol in `props`; bind its `series` to
 * `$datasets.price` (unless the row already supplies a `series` binding), which
 * {@link mergeCanvasManifest} pairs with the synthesized dataset.
 */
export function widgetRowToDefinition(row: CanvasWidgetRow): ComponentDefinition {
  const definition: ComponentDefinition = {
    type: row.widgetType,
    version: row.widgetVersion,
  };
  if (row.props !== undefined && row.props !== null) {
    definition.props = row.props;
  }
  const symbol = priceChartSymbol(row);
  if (symbol) {
    const bindings = objectRecord(row.bindings) ?? {};
    if (!("series" in bindings)) bindings.series = PRICE_SERIES_BINDING;
    definition.bindings = bindings;
  } else if (row.bindings !== undefined && row.bindings !== null) {
    definition.bindings = row.bindings;
  }
  const outputs = outputsRecord(row.outputs);
  if (outputs) definition.outputs = outputs;
  return definition;
}

/**
 * Declare the `$datasets.price` a canvas `price_chart` binds to, when the
 * served manifest does not already carry one.
 *
 * The server's canvas composition emits `datasets: None` (see
 * `crates/api/src/workspaces/publish.rs`), so a `price_chart` placed through
 * the palette would otherwise render with a `series` binding that resolves to
 * nothing. Non-canvas manifests are returned unchanged.
 */
export function withCanvasDatasets(manifest: PageManifest): PageManifest {
  if (!isCanvasManifest(manifest) || manifest.layout.type !== "canvas") {
    return manifest;
  }
  const existing = manifest.datasets ?? {};
  if (existing[PRICE_DATASET_ID]) return manifest;

  const declare = (symbol?: string): PageManifest => ({
    ...manifest,
    datasets: {
      ...existing,
      [PRICE_DATASET_ID]: priceSeriesDataset(symbol?.trim() || "AAPL"),
    },
  });

  // A widget placement whose inputs bind `series` to `$datasets.price`.
  for (const placement of Object.values(manifest.widgets ?? {})) {
    const inputs = objectRecord(placement.inputs);
    if (inputs?.series !== PRICE_SERIES_BINDING) continue;
    const symbol =
      typeof inputs.symbol === "string" ? inputs.symbol : undefined;
    return declare(symbol);
  }

  // A component instance whose bindings bind `series` to `$datasets.price`.
  for (const item of manifest.layout.children) {
    const definition = manifest.components?.[item.widget];
    if (!definition) continue;
    const bindings = objectRecord(definition.bindings);
    if (bindings?.series !== PRICE_SERIES_BINDING) continue;
    const props = objectRecord(definition.props);
    const symbol = typeof props?.symbol === "string" ? props.symbol : undefined;
    return declare(symbol);
  }
  return manifest;
}

/**
 * Build the EFFECTIVE manifest for a canvas tab: the served widget
 * definitions/datasets/state/derivations are kept, the layout is derived from
 * the local (merged) rows, any locally-added widget missing from the served
 * definition map is synthesized from its row, and a `price_chart`'s
 * `$datasets.price` is declared if it is absent. Structured (non-canvas)
 * manifests are returned unchanged.
 */
export function mergeCanvasManifest(
  served: PageManifest,
  rows: readonly CanvasWidgetRow[],
): PageManifest {
  if (!isCanvasManifest(served)) return served;

  const components: Record<string, ComponentDefinition> = {
    ...served.components,
  };
  const widgets: Record<string, ManifestWidgetPlacement> = {
    ...(served.widgets ?? {}),
  };
  const children: CanvasItem[] = [];
  for (const row of rows) {
    children.push(widgetRowToCanvasItem(row));
    if (row.widgetRef) {
      // A widget PLACEMENT: the client expands it against the Postgres widget
      // library before compiling (see `useExpandedManifest`).
      if (!widgets[row.widgetId]) {
        widgets[row.widgetId] = {
          widget: row.widgetRef,
          version: row.widgetVersion,
          inputs: objectRecord(row.inputValues),
        };
      }
    } else if (!components[row.widgetId]) {
      components[row.widgetId] = widgetRowToDefinition(row);
    }
  }

  const servedLayout =
    served.layout.type === "canvas" ? served.layout : undefined;
  return withCanvasDatasets({
    ...served,
    components,
    widgets,
    layout: {
      type: "canvas",
      grid: servedLayout?.grid ?? null,
      children,
    },
  });
}

/** Build the placement lookup `canvasItemsToWidgetOps` diffs against. */
export function widgetPlacements(
  rows: readonly CanvasWidgetRow[],
): Map<string, WidgetPlacement> {
  const placements = new Map<string, WidgetPlacement>();
  for (const row of rows) {
    placements.set(row.widgetId, {
      x: row.x,
      y: row.y,
      w: row.w,
      h: row.h,
      zIndex: row.zIndex,
    });
  }
  return placements;
}

/**
 * Diff React Flow's emitted `CanvasItem`s against the widget rows the local
 * table already holds and produce the optimistic ops for what changed. Widgets
 * absent from `previous` (e.g. palette additions, which apply their own op)
 * are ignored, as is a no-op selection change.
 */
export function canvasItemsToWidgetOps(
  items: readonly CanvasItem[],
  previous: ReadonlyMap<string, WidgetPlacement>,
): WidgetOp[] {
  const ops: WidgetOp[] = [];
  for (const item of items) {
    const prior = previous.get(item.widget);
    if (!prior) continue;

    if (
      Math.abs(item.x - prior.x) > PLACEMENT_EPSILON ||
      Math.abs(item.y - prior.y) > PLACEMENT_EPSILON
    ) {
      ops.push({ kind: "move", widgetId: item.widget, x: item.x, y: item.y });
    }
    if (
      Math.abs(item.w - prior.w) > PLACEMENT_EPSILON ||
      Math.abs(item.h - prior.h) > PLACEMENT_EPSILON
    ) {
      ops.push({ kind: "resize", widgetId: item.widget, w: item.w, h: item.h });
    }
    const zIndex = item.zIndex ?? 0;
    if (zIndex !== (prior.zIndex ?? 0)) {
      ops.push({ kind: "z", widgetId: item.widget, zIndex });
    }
  }
  return ops;
}
